import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const DeleteAccount = () => {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');

  const handleDeleteRequest = async (e) => {
    e.preventDefault();
    setIsDeleting(true);
    
    try {
      const response = await fetch('/backend/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          reason,
          language: i18n.language
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(data.message);
        setShowConfirmation(true);
        setEmail('');
        setReason('');
      } else {
        alert(data.error || (i18n.language === 'fr' 
          ? 'Une erreur est survenue. Veuillez réessayer.' 
          : 'An error occurred. Please try again.'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(i18n.language === 'fr' 
        ? 'Une erreur est survenue. Veuillez réessayer.' 
        : 'An error occurred. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 pt-20">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              {i18n.language === 'fr' ? 'Supprimer mon compte' : 'Delete my account'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'fr' 
                ? 'Nous sommes désolés de vous voir partir. Cette action est irréversible.'
                : 'We\'re sorry to see you go. This action is irreversible.'}
            </p>
          </div>

          {/* Warning Box */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">
                  {i18n.language === 'fr' ? 'Attention' : 'Warning'}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    {i18n.language === 'fr' 
                      ? 'La suppression de votre compte entraînera la perte définitive de :'
                      : 'Deleting your account will permanently remove:'}
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>{i18n.language === 'fr' ? 'Toutes vos données personnelles' : 'All your personal data'}</li>
                    <li>{i18n.language === 'fr' ? 'Votre historique d\'activité' : 'Your activity history'}</li>
                    <li>{i18n.language === 'fr' ? 'Vos préférences et paramètres' : 'Your preferences and settings'}</li>
                    <li>{i18n.language === 'fr' ? 'Toutes les données associées à votre compte' : 'All data associated with your account'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Account Form */}
          {!showConfirmation ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  {i18n.language === 'fr' ? 'Demande de suppression' : 'Deletion request'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {i18n.language === 'fr' 
                    ? 'Pour demander la suppression de votre compte, veuillez remplir le formulaire ci-dessous. Nous traiterons votre demande dans les 30 jours conformément au RGPD.'
                    : 'To request account deletion, please fill out the form below. We will process your request within 30 days in compliance with GDPR.'}
                </p>
              </div>

              <form onSubmit={handleDeleteRequest} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'fr' ? 'Adresse email' : 'Email address'}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder={i18n.language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                  />
                </div>

                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'fr' ? 'Raison de la suppression (optionnel)' : 'Reason for deletion (optional)'}
                  </label>
                  <textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder={i18n.language === 'fr' 
                      ? 'Dites-nous pourquoi vous souhaitez supprimer votre compte...'
                      : 'Tell us why you want to delete your account...'}
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    type="submit"
                    disabled={isDeleting}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
                  >
                    {isDeleting 
                      ? (i18n.language === 'fr' ? 'Envoi...' : 'Sending...')
                      : (i18n.language === 'fr' ? 'Demander la suppression' : 'Request deletion')
                    }
                  </button>
                  <Link
                    to="/"
                    className="text-gray-600 hover:text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                  >
                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Link>
                </div>
              </form>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-green-600 mb-4">
                <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {i18n.language === 'fr' ? 'Demande envoyée' : 'Request sent'}
              </h2>
              <p className="text-gray-600 mb-6">
                {i18n.language === 'fr' 
                  ? 'Nous avons reçu votre demande de suppression. Nous vous contacterons dans les 30 jours pour confirmer la suppression de votre compte.'
                  : 'We have received your deletion request. We will contact you within 30 days to confirm the deletion of your account.'}
              </p>
              <Link
                to="/"
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                {i18n.language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
              </Link>
            </div>
          )}

          {/* Additional Information */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {i18n.language === 'fr' ? 'Informations supplémentaires' : 'Additional information'}
            </h3>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                {i18n.language === 'fr' 
                  ? '• Conformément au RGPD, nous traiterons votre demande dans les 30 jours'
                  : '• In compliance with GDPR, we will process your request within 30 days'}
              </p>
              <p>
                {i18n.language === 'fr' 
                  ? '• Vous recevrez un email de confirmation de votre demande'
                  : '• You will receive an email confirmation of your request'}
              </p>
              <p>
                {i18n.language === 'fr' 
                  ? '• Pour toute question, contactez-nous à support@klipiz.com'
                  : '• For any questions, contact us at support@klipiz.com'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount; 